import { InterviewPreviewOutput, Shift } from '@/api/types.ts';
import { Flex, Grid } from '@keyslabor/shared/ui';
import styled from 'styled-components';
import { useIsMobile } from '@keyslabor/shared/hooks/useBreakPoint.ts';
import React, { FC } from 'react';
import { cn } from '@/lib/utils';
import { Avatar } from 'antd';

const shifts: Record<Shift, string> = {
  am: 'am',
  pm: 'pm',
  overnight: 'overnight',
  flexible: 'flexible',
  late_closer: 'late_closer',
};

const Card = styled(Flex)`
  background-color: white;
  color: black;
  box-shadow: 0px 4px 4px 0px rgba(20, 57, 128, 0.1);
  width: 335px;
  border-radius: 4px;
  font-size: 18px;
  font-weight: 500;
`;

const PositionGrid = styled(Grid)`
  font-size: 18px;
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: 2fr 1fr 1fr;
  width: 100%;
  justify-items: stretch;
`;

const renderInterviewScore = (interview_score: number, className?: string) => {
  if (!interview_score) return <div className={cn('size-6', className)} />;
  return (
    <div
      className={cn(
        'text-white text-center size-6 rounded text-sm font-medium leading-6',
        {
          'bg-[#FE4C4C]': interview_score <= 32,
          'bg-[#FFAE00]': interview_score > 32 && interview_score <= 65,
          'bg-[#219653]': interview_score > 65,
        },
        className
      )}
    >
      {interview_score.toFixed(0)}
    </div>
  );
};

const InterviewLeadInfo: FC<{ data: InterviewPreviewOutput }> = ({ data }) => {
  const isMobile = useIsMobile();
  const isLongShiftTitle = [
    shifts.flexible,
    shifts.late_closer,
    shifts.overnight,
  ].includes(data.shift as Shift);
  const leadName = data.name ?? data.lead_name;
  const scoreFragment = (
    <Flex
      alignItems={'center'}
      fontWeight="500"
      borderRight={isMobile ? null : `1px solid #bfc3db`}
    >
      {!isMobile ? 'Fit score' : null}{' '}
      {renderInterviewScore(data.interview_score, isMobile ? 'mr-3' : 'ml-4')}
    </Flex>
  );
  const leadNameFragment = (
    <Flex
      pr="5px"
      overflow="hidden"
      fontWeight="500"
      css="white-space: nowrap; line-height: 19px;"
      flexGrow={1}
      fontSize="20px"
      flexShrink={1}
      borderRight={isMobile ? null : `1px solid #bfc3db`}
      alignItems={'center'}
    >
      {leadName}
    </Flex>
  );
  const leadImageFragment = (
    <Flex
      width="44px"
      overflow="hidden"
      height="44px"
      borderRadius="100%"
      flexCenter
    >
      <Avatar
        src={data.photo_url}
        size={40}
        css={`
          flex-shrink: 0;
        `}
      >
        {leadName ? leadName[0] : ''}
      </Avatar>
    </Flex>
  );

  const leadPositionFragment = (
    <>
      <Flex
        alignItems="center"
        py="8px"
        flexGrow={1}
        css="& { white-space: nowrap; }"
        borderRight={`1px solid #bfc3db`}
        borderBottom={isMobile ? `1px solid #bfc3db` : null}
        textAlign="left"
        overflow="hidden"
      >
        {data.property}
      </Flex>
      {isMobile && (
        <Flex
          alignItems="center"
          py="8px"
          fontSize="14px"
          flexGrow={1}
          css="& { white-space: nowrap; }"
          borderRight={`1px solid #bfc3db`}
          borderBottom={isMobile ? `1px solid #bfc3db` : null}
          justifyContent="center"
          overflow="hidden"
        >
          Fit Score
        </Flex>
      )}

      <Flex
        alignItems="center"
        pr={'2px'}
        fontSize={isLongShiftTitle ? '14px' : 'inherit'}
        borderBottom={isMobile ? `1px solid #bfc3db` : null}
        borderRight={isMobile ? null : `1px solid #bfc3db`}
        textAlign={isMobile ? 'right' : 'left'}
        justifyContent={isMobile ? 'flex-end' : 'flex-start'}
        css="& {text-transform: uppercase; letter-spacing: -0px; }"
      >
        {data.shift?.replace?.(/_/g, ' ') ?? 'N/A'}
      </Flex>
      <Flex
        py="4px"
        alignItems="center"
        borderRight={`1px solid #bfc3db`}
        flexGrow={1}
        textAlign="left"
        overflow="hidden"
        css="& { white-space: nowrap; }"
      >
        {data.position}
      </Flex>
      {isMobile && (
        <Flex
          py="4px"
          alignItems="center"
          borderRight={`1px solid #bfc3db`}
          flexGrow={1}
          justifyContent="center"
          overflow="hidden"
          css="& { white-space: nowrap; }"
        >
          {renderInterviewScore(data.interview_score)}
        </Flex>
      )}
      <Flex
        alignItems="center"
        justifyContent={isMobile ? 'flex-end' : 'flex-start'}
        py="4px"
        pl={'2px'}
      >
        {data.pay_rate ? '$' + Number(data.pay_rate) : '-'}
      </Flex>
    </>
  );

  if (isMobile) {
    return (
      <Flex flexDirection={'column'} gap={'4px'}>
        <Card
          css={`
            padding: 8px 20px;
            align-items: center;
          `}
        >
          {leadNameFragment}
          {leadImageFragment}
        </Card>
        <Card
          css={`
            padding: 4px 20px;
          `}
        >
          <PositionGrid>{leadPositionFragment}</PositionGrid>
        </Card>
      </Flex>
    );
  } else {
    return (
      <Card
        css={`
          padding: 8px 20px;
          align-items: stretch;
          width: 100%;
          max-width: 1170px;
          margin-bottom: 30px;
          gap: 10px;
          display: grid;
          grid-template-columns: 142px 44px 2fr 2fr 1fr 2fr 1fr;
        `}
      >
        {scoreFragment}
        {leadImageFragment}
        {leadNameFragment}
        {leadPositionFragment}
      </Card>
    );
  }
};

export default InterviewLeadInfo;
